/**
 * AboutMe.jsx  —  redesigned "cosy notebook" About page
 *
 * Drop-in replacement for the existing src/pages/AboutMe.jsx.
 * Requires two new image assets (see README):
 *   src/assets/me.png
 *   src/assets/tuki-crop.png
 *
 * Requires two new Google Fonts added to your index.html / global CSS:
 *   Caveat        (wght 500;600;700)
 *   DM Serif Display (ital 0;1)
 *
 * Suggested <link> tag (add alongside your existing Google Fonts import):
 *   <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@500;600;700&family=DM+Serif+Display:ital@0;1&display=swap" rel="stylesheet">
 */

import { useTheme } from "../context/ThemeContext";
import { SimpleIcon, SOCIAL } from "../components/SocialIcons";
import meImg    from "../assets/me.png";
import tukiImg  from "../assets/tuki-crop.png";

/* ── small inline SVG stickers (original, not brand marks) ── */

function StickerPencil() {
  return (
    <svg viewBox="0 0 64 64" width="44" height="44" style={{ display: "block" }}>
      <defs>
        <filter id="sp"><feDropShadow dx="0" dy="2" stdDeviation="0" floodColor="#1A1410" /></filter>
      </defs>
      <g filter="url(#sp)">
        <rect x="3" y="3" width="58" height="58" rx="14" fill="#FFD9A0" stroke="#1A1410" strokeWidth="3" />
        <rect x="26" y="14" width="12" height="32" rx="3" fill="#FBEFD9" stroke="#1A1410" strokeWidth="2.5" />
        <polygon points="26,46 38,46 32,56" fill="#F2A9A0" stroke="#1A1410" strokeWidth="2.5" strokeLinejoin="round" />
        <rect x="26" y="14" width="12" height="8" rx="2" fill="#A4D4B8" stroke="#1A1410" strokeWidth="2.5" />
      </g>
    </svg>
  );
}

function StickerHeart() {
  return (
    <svg viewBox="0 0 64 64" width="44" height="44" style={{ display: "block" }}>
      <defs>
        <filter id="sh"><feDropShadow dx="0" dy="2" stdDeviation="0" floodColor="#1A1410" /></filter>
      </defs>
      <g filter="url(#sh)">
        <rect x="3" y="3" width="58" height="58" rx="14" fill="#F2A9A0" stroke="#1A1410" strokeWidth="3" />
        <path
          d="M32 48 C 18 38, 14 28, 22 22 C 27 19, 31 22, 32 26 C 33 22, 37 19, 42 22 C 50 28, 46 38, 32 48 Z"
          fill="#FBEFD9" stroke="#1A1410" strokeWidth="2.5" strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

function StickerBrush() {
  return (
    <svg viewBox="0 0 64 64" width="40" height="40" style={{ display: "block" }}>
      <defs>
        <filter id="sb"><feDropShadow dx="0" dy="2" stdDeviation="0" floodColor="#1A1410" /></filter>
      </defs>
      <g filter="url(#sb)">
        <rect x="3" y="3" width="58" height="58" rx="14" fill="#A4D4B8" stroke="#1A1410" strokeWidth="3" />
        <line x1="20" y1="48" x2="44" y2="16" stroke="#1A1410" strokeWidth="3" strokeLinecap="round" />
        <ellipse cx="44" cy="16" rx="6" ry="4" transform="rotate(-30 44 16)" fill="#F2A9A0" stroke="#1A1410" strokeWidth="2.5" />
        <circle cx="22" cy="46" r="5" fill="#FFD9A0" stroke="#1A1410" strokeWidth="2.5" />
      </g>
    </svg>
  );
}

function StickerPotato() {
  return (
    <svg viewBox="0 0 64 64" width="40" height="40" style={{ display: "block" }}>
      <defs>
        <filter id="spo"><feDropShadow dx="0" dy="2" stdDeviation="0" floodColor="#1A1410" /></filter>
      </defs>
      <g filter="url(#spo)">
        <rect x="3" y="3" width="58" height="58" rx="14" fill="#FFD9A0" stroke="#1A1410" strokeWidth="3" />
        <ellipse cx="32" cy="34" rx="16" ry="12" fill="#FBEFD9" stroke="#1A1410" strokeWidth="2.5" />
        <circle cx="24" cy="30" r="2.5" fill="#1A1410" />
        <circle cx="36" cy="28" r="2"   fill="#1A1410" />
        <circle cx="30" cy="38" r="1.5" fill="#1A1410" />
        <circle cx="40" cy="36" r="2"   fill="#1A1410" />
        <line x1="32" y1="22" x2="32" y2="16" stroke="#A4D4B8" strokeWidth="2.5" strokeLinecap="round" />
        <ellipse cx="29" cy="15" rx="3.5" ry="2" transform="rotate(-20 29 15)" fill="#A4D4B8" stroke="#1A1410" strokeWidth="1.5" />
        <ellipse cx="35" cy="14" rx="3.5" ry="2" transform="rotate(20 35 14)"  fill="#A4D4B8" stroke="#1A1410" strokeWidth="1.5" />
      </g>
    </svg>
  );
}

function StickerTV() {
  return (
    <svg viewBox="0 0 64 64" width="44" height="44" style={{ display: "block" }}>
      <defs>
        <filter id="stv"><feDropShadow dx="0" dy="2" stdDeviation="0" floodColor="#1A1410" /></filter>
      </defs>
      <g filter="url(#stv)">
        <rect x="3" y="3" width="58" height="58" rx="14" fill="#F2A9A0" stroke="#1A1410" strokeWidth="3" />
        <rect x="13" y="20" width="38" height="26" rx="4" fill="#FBEFD9" stroke="#1A1410" strokeWidth="2.5" />
        <path d="M27 28 L27 38 L38 33 Z" fill="#1A1410" />
        <line x1="19" y1="50" x2="26" y2="46" stroke="#1A1410" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="45" y1="50" x2="38" y2="46" stroke="#1A1410" strokeWidth="2.5" strokeLinecap="round" />
      </g>
    </svg>
  );
}

/* ── reusable card wrapper ── */
function Card({ children, bg = "#FBEFD9", style = {} }) {
  return (
    <div
      style={{
        background: bg,
        border: "2px solid #2A1E14",
        borderRadius: 16,
        boxShadow: "4px 4px 0 #2A1E14",
        padding: "30px 28px 26px",
        position: "relative",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Kicker({ children, dark }) {
  return (
    <div
      style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: dark ? "#4ade80" : "#0E6E58",
        marginBottom: 10,
        fontWeight: 600,
      }}
    >
      {children}
    </div>
  );
}

function CardTitle({ children, dark }) {
  return (
    <div
      style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: 30,
        fontWeight: 400,
        marginBottom: 10,
        lineHeight: 1.1,
        color: dark ? "#F5E8D0" : "#2A1E14",
      }}
    >
      {children}
    </div>
  );
}

function Aside({ children }) {
  return (
    <p
      style={{
        marginTop: 12,
        fontFamily: "'Caveat', cursive",
        fontSize: 18,
        color: "#A04030",
        lineHeight: 1.4,
      }}
    >
      {children}
    </p>
  );
}

const PERSON_LD = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": "https://5minutenursingschool.com/aboutme#person",
  "name": "Farjana Yasmin Mim",
  "jobTitle": "Nursing Instructor",
  "worksFor": {
    "@type": "Organization",
    "name": "Green Crescent College of Nursing, Dhaka",
  },
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Dhaka",
    "addressCountry": "BD",
  },
  "url": "https://5minutenursingschool.com/aboutme",
  "sameAs": [
    "https://www.youtube.com/@Caffei_N",
    "https://www.instagram.com/caffei_n_",
    "https://www.facebook.com/caffeiN0w0",
    "https://www.pinterest.com/caffei_n",
  ],
  "publisher": { "@id": "https://5minutenursingschool.com/#organization" },
};

/* ── main component ── */
export default function AboutMe() {
  const { dark } = useTheme();

  const bg      = dark ? "#1A1612" : "#FFF8EC";
  const ink     = dark ? "#F5E8D0" : "#2A1E14";
  const muted   = dark ? "#A88862" : "#5C4A3A";

  /* ruled-paper overlay colour */
  const ruleColor = dark
    ? "rgba(255,220,160,0.06)"
    : "rgba(168,136,98,0.13)";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: bg,
        color: ink,
        fontFamily: "'Inter Tight', system-ui, sans-serif",
        position: "relative",
      }}
    >
      <title>About Mim | 5 Minute Nursing School</title>
      <meta name="description" content="Meet Farjana Yasmin Mim — nursing teacher in Dhaka, painter, cat-mom to Tuki, and creator of 5 Minute Nursing School." />
      <link rel="canonical" href="https://5minutenursingschool.com/aboutme" />
      <script type="application/ld+json">{JSON.stringify(PERSON_LD)}</script>
      {/* Ruled paper lines */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          backgroundImage: `repeating-linear-gradient(180deg, transparent 0px 31px, ${ruleColor} 31px 32px)`,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: 720,
          margin: "0 auto",
          padding: "56px 32px 96px",
        }}
      >
        {/* ── Tape strips ── */}
        <div style={{ display: "flex", gap: 32, justifyContent: "center" }}>
          {[60, 80, 48].map((w, i) => (
            <div
              key={i}
              style={{
                width: w,
                height: 18,
                background: "rgba(255,217,160,0.55)",
                border: "1px solid rgba(160,120,60,0.25)",
                borderRadius: 2,
              }}
            />
          ))}
        </div>

        {/* ── Header ── */}
        <header
          style={{
            marginTop: 24,
            display: "flex",
            gap: 28,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          {/* Polaroid portrait */}
          <div style={{ flexShrink: 0, position: "relative", transform: "rotate(-2.5deg)" }}>
            <div
              style={{
                width: 120,
                height: 150,
                background: "#fff",
                border: "2px solid #2A1E14",
                boxShadow: "3px 3px 0 #2A1E14",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "6px 6px 4px",
                gap: 4,
              }}
            >
              <img
                src={meImg}
                alt="Farjana Yasmin Mim"
                style={{ width: "100%", flex: 1, objectFit: "cover", objectPosition: "center top", display: "block" }}
              />
              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "0.10em",
                  color: "#5C4A3A",
                  textAlign: "center",
                  paddingBottom: 2,
                }}
              >
                Mim · Dhaka
              </div>
            </div>
            <div
              style={{
                position: "absolute",
                bottom: -22,
                left: 0,
                right: 0,
                textAlign: "center",
                fontFamily: "'Caveat', cursive",
                fontSize: 15,
                color: muted,
              }}
            >
              Mim ✿ 2025
            </div>
          </div>

          {/* Name + bio */}
          <div style={{ paddingTop: 6, flex: 1, minWidth: 260 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 12px",
                background: "#A4D4B8",
                border: "2px solid #2A1E14",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                boxShadow: "2px 2px 0 #2A1E14",
                color: "#2A1E14",
              }}
            >
              🩺 Hello, I'm Mim
            </div>

            <h1
              style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: 52,
                lineHeight: 1.0,
                letterSpacing: "-0.01em",
                marginTop: 14,
                fontWeight: 400,
                color: ink,
              }}
            >
              Farjana
              <br />
              <em style={{ fontStyle: "italic", color: dark ? "#4ade80" : "#0E6E58" }}>Yasmin Mim.</em>
            </h1>

            <p style={{ marginTop: 10, fontSize: 15, lineHeight: 1.6, color: muted, maxWidth: 480 }}>
              Teacher. Note-maker. Cat-mom.
              <br />
              Sharing everything so you don't have to suffer alone.
            </p>

            {/* Credential chips */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
              {["MSc Nursing · GCU", "Registered Nurse · BNMC", "Instructor · GCCN"].map((t) => (
                <span
                  key={t}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 999,
                    border: `1.5px solid ${ink}`,
                    background: dark ? "#2A2018" : "#FFF8EC",
                    fontSize: 13,
                    fontWeight: 600,
                    color: ink,
                  }}
                >
                  {t}
                </span>
              ))}
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "6px 14px",
                  borderRadius: 999,
                  border: `1.5px solid ${ink}`,
                  background: dark ? "#2A2018" : "#FFF8EC",
                  fontSize: 13,
                  fontWeight: 600,
                  color: ink,
                }}
              >
                <span style={{ color: "#e05555" }}>📍</span> Dhaka
              </span>
            </div>
          </div>
        </header>

        {/* ── Divider ── */}
        <hr style={{ margin: "48px 0 0", border: "none", borderTop: `1.5px solid ${ink}` }} />

        {/* ── Cards ── */}
        <div style={{ marginTop: 40, display: "flex", flexDirection: "column", gap: 28 }}>

          {/* Card 1 — Who I am */}
          <Card bg={dark ? "#231C14" : "#fff"}>
            <div style={{ position: "absolute", top: -18, left: 24 }}><StickerPencil /></div>
            <Kicker dark={dark}>Who I am</Kicker>
            <p style={{ fontSize: 15, lineHeight: 1.72, color: ink }}>
              Hi! I'm a teacher navigating the wonderfully chaotic world of healthcare education in Dhaka, Bangladesh.
              I started this site because study notes shouldn't cost you your sanity or your printer ink.
              I believe in keeping things <strong>short, clear and actually useful</strong>.... because at 2 a.m. before
              clinicals, you need answers, not essays.
            </p>
            <Aside>"No 40-page PDFs when a 5-page one will do." ✏️</Aside>
          </Card>

          {/* Card 2 — Tuki */}
          <Card bg={dark ? "#2A1A1A" : "#FFF0F0"}>
            <div style={{ position: "absolute", top: -18, left: 24 }}><StickerHeart /></div>
            <Kicker dark={dark}>My Tuki 🩷</Kicker>
            <CardTitle dark={dark}>The real boss around here.</CardTitle>
            <div style={{ display: "flex", gap: 20, alignItems: "flex-start", marginTop: 10, flexWrap: "wrap" }}>
              {/* Tuki polaroid */}
              <div style={{ flexShrink: 0, transform: "rotate(2.5deg)" }}>
                <div
                  style={{
                    background: "#fff",
                    border: "2px solid #2A1E14",
                    boxShadow: "3px 3px 0 #2A1E14",
                    padding: "6px 6px 4px",
                    width: 130,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <img
                    src={tukiImg}
                    alt="Tuki the cat"
                    style={{ width: 118, height: 118, objectFit: "cover", display: "block" }}
                  />
                  <div
                    style={{
                      fontFamily: "'Caveat', cursive",
                      fontSize: 14,
                      textAlign: "center",
                      color: "#5C4A3A",
                      paddingBottom: 2,
                    }}
                  >
                    Tuki 🩷
                  </div>
                </div>
              </div>

              <div style={{ flex: 1, minWidth: 180 }}>
                <p style={{ fontSize: 15, lineHeight: 1.72, color: ink }}>
                  Meet <strong>Tuki</strong> — my cat, my emotional support animal, and honestly the reason this
                  site looks as good as it does. She has <em>standards.</em> I just try to keep up.
                  There's will be whole gallery dedicated to her, as there should be.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                  {["Chief Quality Officer", "Nap Supervisor", "Unconditional Love"].map((t) => (
                    <span
                      key={t}
                      style={{
                        padding: "5px 12px",
                        borderRadius: 999,
                        fontSize: 13,
                        fontWeight: 600,
                        border: "1.5px solid #2A1E14",
                        background: "#F2A9A0",
                        color: "#2A1E14",
                      }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Cards 3+4 — Paintings + Potatoes */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 20,
            }}
          >
            <Card bg={dark ? "#151F18" : "#F0F7F2"}>
              <div style={{ position: "absolute", top: -16, left: 20 }}><StickerBrush /></div>
              <Kicker dark={dark}>Paintings</Kicker>
              <CardTitle dark={dark}>Mostly mountains.</CardTitle>
              <p style={{ fontSize: 14, lineHeight: 1.72, color: ink }}>
                Acrylic paint is my antidote to the very precise world of medicine. Soft landscapes,
                the occasional dramatic ocean, and a suspicious amount of mountain imagery.
                There's a gallery — go look.
              </p>
              <Aside>messy hands, happy brain 🎨</Aside>
            </Card>

            <Card bg={dark ? "#1F1C10" : "#FFFBF0"}>
              <div style={{ position: "absolute", top: -16, left: 20 }}><StickerPotato /></div>
              <Kicker dark={dark}>Truly important</Kicker>
              <CardTitle dark={dark}>I love potatoes. 🥔</CardTitle>
              <p style={{ fontSize: 14, lineHeight: 1.72, color: ink }}>
                Alu. Aloo. Potato. Call them what you want — I will eat them every way imaginable.
                This is <strong>relevant information</strong> about me as a person.
                Thank you for understanding.
              </p>
              <Aside>boiled, fried, mashed — yes 🥔</Aside>
            </Card>
          </div>

          {/* Card 5 — YouTube / Caffei_N */}
          <Card bg={dark ? "#1A1612" : "#FBEFD9"}>
            <div style={{ position: "absolute", top: -18, left: 24 }}><StickerTV /></div>
            <Kicker dark={dark}>On YouTube</Kicker>
            <CardTitle dark={dark}>Come say hello.</CardTitle>
            <p style={{ fontSize: 15, lineHeight: 1.72, color: ink }}>
              I run a YouTube channel as <strong>Caffei_N</strong> — notes, study tips, and the occasionally
              unhinged study session in video form. The content is more organised than my sleep schedule,
              I promise.
            </p>

            {/* Social links — simple icon + handle */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                marginTop: 16,
              }}
            >
              {SOCIAL.map(({ key, handle, href }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "7px 14px",
                    background: dark ? "#2A2018" : "#fff",
                    border: `2px solid ${ink}`,
                    borderRadius: 12,
                    fontSize: 13,
                    fontWeight: 600,
                    color: ink,
                    textDecoration: "none",
                    boxShadow: `2px 2px 0 ${ink}`,
                    transition: "transform 0.15s, box-shadow 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = `3px 4px 0 ${ink}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow = `2px 2px 0 ${ink}`;
                  }}
                >
                  <SimpleIcon name={key} size={18} />
                  {handle}
                </a>
              ))}
            </div>
          </Card>

        </div>{/* /cards */}

        {/* ── Footer ── */}
        <footer
          style={{
            marginTop: 64,
            paddingTop: 22,
            borderTop: `1.5px solid ${ink}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.12em",
            color: muted,
          }}
        >
          <span>© 2025 · 5 Minute Nursing School</span>
          <span style={{ fontFamily: "'Caveat', cursive", fontSize: 16, color: "#A04030" }}>
            made with ☕ in Dhaka
          </span>
        </footer>

      </div>
    </div>
  );
}
